import { PurchaseOrderRowDetailPanel, purchaseOrderRowHistoryColumn, createPurchaseOrderRowEditColumn } from './PurchaseOrderRowComponents';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { GridColDef, GridRowParams, GridRenderCellParams, useGridApiContext } from '@mui/x-data-grid-premium';

import { useTableSettings, tableSettingsProps, useInitialTableSettings, useCompleteTableSettings } from 'src/core/tables/useTableSettings';
import { useTableSelector, MergeResult } from 'src/core/tables/useTableSelector';
import tableHelper from 'src/core/tables/tableHelper';
import useUpdateEffect from 'src/shared/helpers/useUpdateEffect';
import { createPurchaseOrderPolicy, getPurchaseOrderRowStatus } from './PurchaseOrderRules';
import { GridActionsCellItem } from '@mui/x-data-grid-premium';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGetSupplierProductsBySupplierId } from 'src/pages/purchasing/purchasingRequests';

import { Stack, TextField, InputAdornment } from '@mui/material';


export function useSelectProductsOnPurchaseOrder( initialValues: any, supplierId: any, saveResult?: any, opts?: { isExternalWarehouse?: boolean; onRowsChanged?: () => void; onEditRow?: (row: any) => void; onRowFormError?: (message: string) => void } ): [ GridColDef[], tableSettingsProps, any[], ReturnType<typeof useTableSelector>, () => any[], (rowId: string | number, patch: { Quantity: number; NegotiatedPrice: number }) => Promise<any> ] {
  const [rows, setRows] = useState<any[]>(initialValues?.PurchaseOrderRows ?? []);

  // hard reset coordination after save
  const pendingTableResetRef = useRef(false);
  useUpdateEffect(() => {
    if (initialValues?.PurchaseOrderRows !== undefined && initialValues?.PurchaseOrderRows !== null) setRows(initialValues.PurchaseOrderRows);
  }, [initialValues?.PurchaseOrderRows]);
  const policy = useMemo( () => createPurchaseOrderPolicy({ statusId: initialValues?.Status ?? initialValues?.StatusId, rows, env: { isExternalWarehouse: opts?.isExternalWarehouse ?? false } }), [initialValues?.Status, initialValues?.StatusId, rows, opts?.isExternalWarehouse] );
  const editRowRef = useRef<(row: any) => void>();
  const editRow = useCallback((row: any) => editRowRef.current?.(row), []);
  const [dispCols, dispSettings] = usePurchaseOrderRowsTable({ rows, setRows, id: initialValues?.PurchaseOrderId, onEditRow: opts?.onEditRow ? editRow : undefined, policy });
  const [selCols, selSettings] = usePurchaseProductsSelectTable(supplierId);

  // Always build submit rows from the display grid "truth" when possible
  const getRowsForSubmit = () => {
    const apiRef = dispSettings?.apiRef;
    const rowModels = apiRef?.current?.getRowModels?.();
    const gridRows = rowModels ? Array.from(rowModels.values()) : null;
    return (gridRows ?? dispSettings?.allRows ?? rows ?? []) as any[];
  };
  // Try to normalize whatever "result" shape comes back into "server PO rows"
  const extractSavedRows = (r: any) => {
    if (!r) return undefined;

    // Common patterns:
    // - { PurchaseOrderRows: [...] }
    // - { data: { PurchaseOrderRows: [...] } }
    // - submit returns array of responses, last one is PO response
    // - { results: [...] } / { responses: [...] }
    const direct = r?.PurchaseOrderRows;
    if (Array.isArray(direct)) return direct;
    const data = r?.data?.PurchaseOrderRows;
    if (Array.isArray(data)) return data;
    const candidates: any[] = Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : Array.isArray(r?.responses) ? r.responses : Array.isArray(r?.data) ? r.data : [];
    for (let i = candidates.length - 1; i >= 0; i--) {
      const c = candidates[i];
      const cRows = c?.PurchaseOrderRows ?? c?.data?.PurchaseOrderRows;
      if (Array.isArray(cRows)) return cRows;
    }
    return undefined;
  };

  // On successful save: swap to server truth + clear selections
  useUpdateEffect(() => {
    if (!saveResult) return;
    const savedRows = extractSavedRows(saveResult);
    if (!Array.isArray(savedRows)) return;
    pendingTableResetRef.current = true;
    setRows(savedRows);
    dispSettings?.setRowSelectionModel?.([]);
    selSettings?.setRowSelectionModel?.([]);
  }, [saveResult]);

  // After rows commit: clear internal edits and force grid to adopt the rows
  useEffect(() => {
    if (!pendingTableResetRef.current) return;
    pendingTableResetRef.current = false;
    dispSettings?.discardAllChanges?.();
    dispSettings?.apiRef?.current?.setRows?.(rows);
    dispSettings?.setRowSelectionModel?.([]);
    selSettings?.setRowSelectionModel?.([]);
  }, [rows, dispSettings, selSettings]);

  // If supplier changes, reset selector selection (keeps UX consistent)
  useEffect(() => { selSettings?.setRowSelectionModel?.([]); }, [String(supplierId ?? '')]);

  const mergeProductsIntoRows = (prev: any[], picked: any[]): MergeResult<any> => {
    if (!picked.length) return prev;
    const next = [...prev];
    let nextTempId = Math.min(0, ...next.map((r) => r.PurchaseOrderRowId ?? 0)) - 1;
    picked.forEach((item) => {
      if (next.some((r) => String(r.SupplierAssortmentItemId ?? r.VendorAssortmentItemId) === String(item.SupplierAssortmentItemId))) return;
      const list = item.Price ?? item.CurrencyConvertedPrice ?? 0;
      const negotiated = list;
      const amount = round(Math.max(0, list - negotiated), 2);
      const rateDecimal = list ? round(amount / list, 6) : 0;
      next.push({
        Quantity: 1,
        ListPriceDefaultCurrency: list,
        NegotiatedPrice: negotiated,
        DiscountRate: rateDecimal, // DECIMAL
        DiscountAmount: amount,
        SumRequestedQty: round(1 * negotiated, 2),
        RowTotalOrderCurrency: list,
        ManufProductId: item.ManufacturerProduct?.Key ?? null,
        VendorAssortmentItemId: item.SupplierAssortmentItemId,
        PurchaseOrderRowId: nextTempId--,
        ...item
      });
    });
    opts?.onRowsChanged?.();
    return next;
  };

  const selector = useTableSelector<any>({
    rows,
    setRows,
    selectGrid: [selCols, selSettings],
    getId: (row: any) => row.SupplierAssortmentItemId,
    onMerge: mergeProductsIntoRows,
    rowId: 'PurchaseOrderRowId',
    displayTableSettings: dispSettings,
    onCommitFunction: (updated) => {
      const prev = rows.find((r) => r.PurchaseOrderRowId === updated?.PurchaseOrderRowId) ?? {};
      const normalized = normalizeFromFinal(prev, updated);
      opts?.onRowsChanged?.();
      return normalized;
    }
  });

  // Wait for the grid's own commit pipeline before reading or replacing a row.
  const finishTableEdits = async () => {
    const api = dispSettings.apiRef?.current;
    const edits = (api?.state as any)?.editRows ?? {};
    for (const [id, fields] of Object.entries(edits)) {
      for (const [field, cell] of Object.entries(fields as Record<string, any>)) {
        if (cell.error || cell.isProcessingProps) throw new Error('Finish the current table edit before using the row form.');
        if (api?.getCellMode?.(id, field) === 'edit') api.stopCellEditMode({ id, field });
      }
    }
    for (let attempt = 0; attempt < 30; attempt++) {
      if (!Object.keys((api?.state as any)?.editRows ?? {}).length) return;
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }
    throw new Error('Finish the current table edit before using the row form.');
  };

  const loadRowIntoForm = async (row: any) => {
    try {
      await finishTableEdits();
      const api = dispSettings.apiRef?.current;
      const live = api?.getRow?.(api.getRowId(row));
      if (!live) throw new Error('This row is no longer in the table.');
      if (policy.rowEditorReadOnlyReason(live)) return;
      opts?.onEditRow?.(live);
    } catch (error) {
      opts?.onRowFormError?.(error instanceof Error ? error.message : 'Could not load the row.');
    }
  };

  editRowRef.current = (row) => {
    void loadRowIntoForm(row);
  };

  const applyRowFromForm = async (rowId: string | number, patch: { Quantity: number; NegotiatedPrice: number }) => {
    await finishTableEdits();
    const api = dispSettings.apiRef?.current;
    const models = api?.getRowModels?.();
    const currentRows: any[] = models ? Array.from(models.values()) : rows;
    const current = currentRows.find((row) => String(row.PurchaseOrderRowId) === String(rowId));
    if (!current) throw new Error('This row is no longer in the table. Select another row.');
    const list = Number(current.ListPriceOrderCurrency ?? current.ListPriceDefaultCurrency) || 0;
    if (!Number.isFinite(patch.Quantity) || patch.Quantity <= 0) throw new Error('Enter a quantity greater than 0.');
    if (!Number.isFinite(patch.NegotiatedPrice) || patch.NegotiatedPrice < 0 || (list > 0 && patch.NegotiatedPrice > list))
      throw new Error('Enter a price between 0 and the list price.');
    const normalized = normalizeFromFinal(current, patch);
    const updated = { ...current, ...patch, ...normalized, ItemPriceOrderCurrency: normalized.NegotiatedPrice };
    // Use the same change tracking as inline edits when supplied by the selector.
    const processRowUpdate = (dispSettings as any).processRowUpdate;
    const committed = processRowUpdate ? await processRowUpdate(updated, current, { rowId: api?.getRowId?.(current) }) : updated;
    const next = { ...updated, ...committed };
    setRows(currentRows.map((row) => (String(row.PurchaseOrderRowId) === String(rowId) ? next : row)));
    api?.updateRows?.([next]);
    opts?.onRowsChanged?.();
    return next;
  };

  return [dispCols, dispSettings, rows, selector, getRowsForSubmit, applyRowFromForm];
}

export const usePurchaseProductsSelectTable = (supplierId: string | number): readonly [GridColDef[], tableSettingsProps] => {
  const { rows, status } = useGetSupplierProductsBySupplierId(supplierId);
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'SupplierAssortmentItemId', headerName: 'Id', width: 90, hideable: false },
      { field: 'Product_Value', headerName: 'Product', flex: 1, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'Product_Key', headerName: 'Product Id', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'SupplierProduct_Value', headerName: 'Supplier Product', flex: 1, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'SupplierProduct_Key', headerName: 'Supplier Product Id', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'ManufacturerProduct_Key', headerName: 'Manufacturer Product Id', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'Price', headerName: 'Price', type: 'number' },
      { field: 'QuantityAvailableAtSupplier', headerName: 'Qty at Supplier', type: 'number' },
      { field: 'Status_Value', headerName: 'Status', valueGetter: tableHelper.getFieldKeyOrValue, cellClassName: tableHelper.statusColumn, renderCell: tableHelper.renderStatusCell }
    ],
    []
  );
  const tableSettings = useTableSettings({ columns, rows, idPath: ['SupplierAssortmentItemId'], tableName: 'PurchaseProductSelectTable' + (supplierId ?? ''), status });
  useEffect(() => { tableSettings.setRowSelectionModel([]); }, []);
  tableSettings.hasToolbar = false;
  tableSettings.checkboxSelection = true;
  return [columns, tableSettings] as const;
};

export const usePurchaseOrderRowsTable = ({ rows, setRows, id, onEditRow = undefined as ((row: any) => void) | undefined, policy = createPurchaseOrderPolicy({ env: { isExternalWarehouse: false } }) }): readonly [GridColDef[], tableSettingsProps] => {
  const idPath = ['PurchaseOrderRowId'];
  const initialTableSettings = useInitialTableSettings({ tableName: 'PurchaseOrderRowsTable' + (id ?? 'Create'), idPath, setExternalRows: setRows }, rows);

  // Show DiscountRate as PERCENT string but keep row value stored as DECIMAL (0.2 = 20%)
  const percentValueGetter = (_: any, row: any) => {
    const dec = Number(row?.DiscountRate);
    if (!Number.isFinite(dec)) return '';
    const pct = Math.round(dec * 100 * 100) / 100; // 2 decimals
    return String(pct);
  };
  const columns: GridColDef[] = useMemo(
    () => [
      purchaseOrderRowHistoryColumn,
      ...(onEditRow ? [createPurchaseOrderRowEditColumn(onEditRow, policy.rowEditorReadOnlyReason)] : []),
      { field: 'Status_Value', headerName: 'Row Status', width: 110, valueGetter: (_value, row) => getPurchaseOrderRowStatus(row), renderCell: tableHelper.renderStatusCell },
      tableHelper.createStackedColumn( { field: 'Product_Key', headerName: 'Article Number', type: 'number', valueGetter: tableHelper.getFieldKeyOrValue, width: 180 }, { field: 'SupplierProduct_Key', headerName: 'Supplier Product No', valueGetter: tableHelper.getFieldKeyOrValue } ),
      tableHelper.createStackedColumn( { field: 'Product_Value', headerName: 'Product Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 220 }, { field: 'SupplierProduct_Value', headerName: 'Supplier Product Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 220 } ),
      { field: 'Quantity', headerName: 'Quantity', type: 'number', editable: true },
      { field: 'QuantityReceived', headerName: 'Qty Received', type: 'number', width: 120 },
      { field: 'NegotiatedPrice', headerName: 'Negotiated Price', type: 'number', valueGetter: (_v, row) => { const explicit = Number(row?.NegotiatedPrice ?? row?.ItemPriceOrderCurrency); return Number.isFinite(explicit) ? explicit : computeNegotiatedFromCostMinusDiscount(row); }, editable: true, width: 220, renderEditCell: (params) => <NegotiatedPriceEditCellInline params={params} apiRef={initialTableSettings.apiRef} /> },

      // Simpler stacked columns: Amount (number) + Rate (%) shown as percent string
      tableHelper.createStackedColumn( { field: 'DiscountAmount', headerName: 'Discount Amount', type: 'number', width: 140 }, { field: 'DiscountRate', headerName: 'Rate (%)', type: 'number', width: 120, valueGetter: percentValueGetter } ),
      { field: 'ETA', headerName: 'ETA', type: 'dateTime', editable: true, valueGetter: tableHelper.getDateColumn },
      { field: 'RTA', headerName: 'RTA', type: 'dateTime', editable: true, valueGetter: tableHelper.getDateColumn },
      { field: 'Comment', headerName: 'Comment', editable: true, minWidth: 180 },
      { field: 'ListPriceOrderCurrency', headerName: 'List Price', type: 'number', valueGetter: (_value, row) => row.ListPriceOrderCurrency ?? row.ListPriceDefaultCurrency },
      { field: 'RowTotalOrderCurrency', headerName: 'Cost', type: 'number' },
      { field: 'ManufProductId', headerName: 'Manuf Product Id', flex: 1 },
      { field: 'SupplierAssortmentItemId', headerName: 'Supplier Assortment ItemId' },
      { field: 'PurchaseOrderRowId', headerName: 'Purchase Order Row Id', renderCell: tableHelper.idCell },
      { field: 'actions', getActions: (params: GridRowParams) => [ <GridActionsCellItem key="delete" icon={<DeleteIcon />} label="Delete" disabled={!policy.action('Delete_Row', params.row)} onClick={() => setRows(rows.filter((row) => row.PurchaseOrderRowId !== params.row.PurchaseOrderRowId))} /> ], type: 'actions', headerName: 'Delete', width: 80 }
    ],
    [rows, setRows, initialTableSettings.apiRef, onEditRow, policy]
  );
  const tableSettings = useCompleteTableSettings({ columns }, initialTableSettings);
  tableSettings.autoHeight = true;
  tableSettings.hasToolbar = false;
  tableSettings.density = 80;
  tableSettings.loading = false;

  /* Show both charts; auto height also supports the stacked layout on narrow cards. */
  tableSettings.getDetailPanelContent = (row: any) => <PurchaseOrderRowDetailPanel row={row} />;
  (tableSettings as tableSettingsProps & { getDetailPanelHeight: () => 'auto' }).getDetailPanelHeight = () => 'auto';
  return [columns, tableSettings] as const;
};

const round = (n: number, dp = 2) => {
  const f = Math.pow(10, dp);
  return Math.round((Number(n) || 0) * f) / f;
};
const clamp = (n: number, min: number, max: number) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return min;
  return Math.min(Math.max(x, min), max);
};
const parseNumLoose = (s: string) => {
  if (s == null) return NaN;
  const cleaned = String(s).replace(',', '.').trim();
  if (cleaned === '') return NaN;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};
const stripLeadingZeroIfMultiDigit = (s: string) => (/^0\d+$/.test(s) ? String(Number(s)) : s);
const formatNumber = (n: number | undefined | null, dp = 2) => (!Number.isFinite(n as number) ? '' : String(round(Number(n), dp)));

const getUnitCost = (row: any) => {
  const list = Number(row?.ListPriceOrderCurrency ?? row?.ListPriceDefaultCurrency);
  const qty = Number(row?.Quantity) || 0;
  const costTotal = Number(row?.RowTotalOrderCurrency);
  if (Number.isFinite(list) && list > 0) return list;
  if (Number.isFinite(costTotal)) return qty > 0 ? round(costTotal / qty, 2) : costTotal;
  return 0;
};

export const computeNegotiatedFromCostMinusDiscount = (row: any) => {
  const unitCost = getUnitCost(row);
  const discAmt = Number(row?.DiscountAmount);
  const discRate = Number(row?.DiscountRate);
  let negotiated = unitCost;
  if (Number.isFinite(discAmt)) negotiated = unitCost - Math.max(0, discAmt);
  else if (Number.isFinite(discRate) && discRate > 0) negotiated = unitCost * (1 - clamp(discRate, 0, 1));
  return round(clamp(negotiated, 0, unitCost > 0 ? unitCost : Number.POSITIVE_INFINITY), 2);
};

// Prefer amount → decimal; fallback to stored decimal; then final/list; else 0
const rateDecimalFromRow = (row: any, list: number) => {
  const amt = Number(row?.DiscountAmount);
  const decStored = Number(row?.DiscountRate);
  const fin = Number(row?.NegotiatedPrice ?? row?.ItemPriceOrderCurrency);
  if (Number.isFinite(amt) && list > 0) return clamp(amt / list, 0, 1);
  if (Number.isFinite(decStored)) return clamp(decStored, 0, 1);
  if (Number.isFinite(fin) && list > 0) return clamp((list - fin) / list, 0, 1);
  return 0;
};

// Normalize on edit end
function normalizeFromFinal(prevRow: any, patch: any) {
  const merged = { ...(prevRow || {}), ...(patch || {}) };
  const list = Number(merged?.ListPriceOrderCurrency ?? merged?.ListPriceDefaultCurrency) || 0;
  const qty = Number(merged?.Quantity) || 0;
  let negotiated = Number(merged?.NegotiatedPrice ?? merged?.ItemPriceOrderCurrency ?? computeNegotiatedFromCostMinusDiscount(merged));
  const outOfRange = !Number.isFinite(negotiated) || negotiated < 0 || (list > 0 && negotiated > list);
  if (outOfRange) negotiated = list;
  const amount = round(Math.max(0, list - negotiated), 2);
  const rateDecimal = list ? round(amount / list, 6) : 0;
  const sum = round(qty * negotiated, 2);
  return { NegotiatedPrice: round(negotiated, 2), ItemPriceOrderCurrency: round(negotiated, 2), DiscountAmount: amount, DiscountRate: rateDecimal, SumRequestedQty: sum };
}

type Mode = 'final' | 'percent';

// The two-input grid editor needs focus, keyboard commit/cancel and adornments not exposed by CustomField.
export function NegotiatedPriceEditCellInline({ params, apiRef: apiRefFromProps }: { params: GridRenderCellParams; apiRef?: any }) {
  const gridApiContextRef = useGridApiContext();
  const apiRef = apiRefFromProps ?? gridApiContextRef;
  const id = params.id;
  const field = params.field;
  const hasFocus = (params as any).hasFocus ?? false;

  // Read live row to seed inputs correctly on open
  const getLiveRow = () => apiRef?.current?.getRow?.(id) ?? params.row ?? {};
  const live = getLiveRow();
  const list = Number(live?.ListPriceOrderCurrency ?? live?.ListPriceDefaultCurrency) || 0;
  const qty = Number(live?.Quantity) || 0;
  const deriveFinal = (row: any) => {
    const l = Number(row?.ListPriceOrderCurrency ?? row?.ListPriceDefaultCurrency) || 0;
    const explicit = Number(row?.NegotiatedPrice ?? row?.ItemPriceOrderCurrency);
    return Number.isFinite(explicit) ? explicit : computeNegotiatedFromCostMinusDiscount(row);
  };
  const initialFinal = deriveFinal(live);
  const initialRateDec = rateDecimalFromRow(live, list);
  const [mode, setMode] = React.useState<Mode>('final');
  const [finalInput, setFinalInput] = React.useState<string>(formatNumber(initialFinal, 2));
  const [percentInput, setPercentInput] = React.useState<string>(formatNumber(rateDecimalFromRow(live, list) * 100, 2));
  // On mount & when id changes, reseed from latest row (fixes 0% on open)
  useEffect(() => {
    const row = getLiveRow();
    const l = Number(row?.ListPriceOrderCurrency ?? row?.ListPriceDefaultCurrency) || 0;
    const effFinal = deriveFinal(row);
    const rateDec = rateDecimalFromRow(row, l);
    setFinalInput(formatNumber(effFinal, 2));
    setPercentInput(formatNumber(rateDec * 100, 2));
  }, [id]);

  // Also react to external row changes while editing
  useEffect(() => {
    const row = getLiveRow();
    const l = Number(row?.ListPriceOrderCurrency ?? row?.ListPriceDefaultCurrency) || 0;
    const effFinal = deriveFinal(row);
    const rateDec = rateDecimalFromRow(row, l);
    setFinalInput(formatNumber(effFinal, 2));
    setPercentInput(formatNumber(rateDec * 100, 2));
  }, [params.row?.NegotiatedPrice, params.row?.DiscountRate, params.row?.DiscountAmount]);
  const stopEdit = (commit: boolean) => apiRef?.current?.stopCellEditMode?.({ id, field, ignoreModifications: !commit });
  const clampNegotiated = (n: number) => {
    if (!Number.isFinite(n)) return list;
    if (n < 0) return 0;
    if (list > 0 && n > list) return list;
    return round(n, 2);
  };
  const computeFromPercent = (pctNum: number) => clampNegotiated(round(list * (1 - pctNum / 100), 2));
  const writeDerivedFields = (negotiated: number) => {
    const amount = round(Math.max(0, list - negotiated), 2);
    const rateDecimal = list ? round(amount / list, 6) : 0;
    const sum = round((Number.isFinite(qty) ? qty : 0) * negotiated, 2);
    apiRef?.current?.updateRows?.([{ id, NegotiatedPrice: negotiated, DiscountAmount: amount, DiscountRate: rateDecimal, SumRequestedQty: sum }]);
  };

  // Switching field focus reconciles the other input (no commit yet)
  const chooseMode = (m: Mode) => {
    if (m === mode) return;
    if (m === 'final') {
      const pct = parseNumLoose(percentInput);
      if (Number.isFinite(pct)) setFinalInput(formatNumber(computeFromPercent(pct), 2));
    } else {
      const fin = parseNumLoose(finalInput);
      if (Number.isFinite(fin) && list > 0) setPercentInput(formatNumber((Math.max(0, list - fin) / list) * 100, 2));
    }
    setMode(m);
  };

  const commitValues = () => {
    const negotiated =
      mode === 'final'
        ? clampNegotiated(parseNumLoose(finalInput))
        : (() => {
          const pct = parseNumLoose(percentInput);
          return Number.isFinite(pct) ? computeFromPercent(pct) : list;
        })();
    writeDerivedFields(negotiated);
    apiRef?.current?.setEditCellValue?.({ id, field: 'NegotiatedPrice', value: negotiated, debounceMs: 0 });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitValues();
      stopEdit(true);
    }
    if (e.key === 'Escape') stopEdit(false);
  };

  return (
    <Stack spacing={0.5} sx={{ width: 1, py: 0.25 }}>
      <TextField value={finalInput} onFocus={() => chooseMode('final')} onChange={(e) => setFinalInput(stripLeadingZeroIfMultiDigit(e.target.value ?? ''))} onKeyDown={handleKeyDown} onBlur={commitValues} size="small" variant="outlined" type="text" inputMode="decimal" placeholder="Final" fullWidth autoFocus={hasFocus && mode === 'final'} sx={{ '& fieldset': { border: 'none' }, py: 0 }} />
      <TextField value={percentInput} onFocus={() => chooseMode('percent')} onChange={(e) => setPercentInput(stripLeadingZeroIfMultiDigit(e.target.value ?? ''))} onKeyDown={handleKeyDown} onBlur={commitValues} size="small" variant="outlined" type="text" inputMode="decimal" placeholder="%" fullWidth autoFocus={hasFocus && mode === 'percent'} sx={{ '& fieldset': { border: 'none' }, py: 0 }} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
    </Stack>
  );
}

export { ProductPurchaseHistoryTable, useProductPurchaseHistoryTable } from './PurchaseOrderRowComponents';
